import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapBankAccount = (item, index, language = "en") => {
  const isArabic = language === "ar";
  const title = stripHtml((isArabic ? (item.title_ar || item.Title_ar || item.titleAr || item.TitleAr) : null) || item.title_en || item.Title_en || item.title || item.Title);
  const categoryTitle = stripHtml((isArabic ? (
    item.category_title_ar || item.Category_title_ar || item.categoryTitle_ar || item.CategoryTitle_ar ||
    item.categorytitle_ar || item.Categorytitle_ar || item.categoryTitleAr || item.CategoryTitleAr
  ) : null) || item.category_title_en || item.Category_title_en || item.categoryTitle || item.CategoryTitle);

  return {
    id: item.id || String(index + 1),
    title,
    categoryTitle,
    accountNumber: stripHtml(item.acno || item.accountNumber),
    iban: stripHtml(item.iban),
  };
};

const getAccountIcon = (categoryTitle) => (
  /zakat|زكاة/i.test(categoryTitle || "") ? "fa-star-and-crescent" : "fa-hand-holding-dollar"
);

export default function BanksAccounts({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [copiedValue, setCopiedValue] = useState("");

  useEffect(() => {
    document.title = isArabic ? "الحسابات البنكية | مؤسسة تراحم الخيرية" : "Banks Accounts | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadAccounts = async () => {
      setIsLoading(true);
      try {
        setLoadError("");

        const response = await fetch(getApiUrl("/api/GetBankAcList.ashx"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Bank accounts request failed with ${response.status}`);

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        setAccounts(rows.map((item, index) => mapBankAccount(item, index, language)).filter((item) => item.title && (item.accountNumber || item.iban)));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load bank accounts", error);
          setLoadError(isArabic ? "تعذر تحميل الحسابات البنكية حالياً. يرجى المحاولة لاحقاً." : "Unable to load bank accounts right now. Please try again later.");
          setAccounts([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadAccounts();
    return () => controller.abort();
  }, [isArabic, language]);

  const copyValue = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedValue(value);
      window.setTimeout(() => setCopiedValue(""), 1600);
    } catch (error) {
      console.warn("Unable to copy bank detail", error);
    }
  };

  const visibleAccounts = accounts;

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{isArabic ? "الحسابات البنكية" : "Banks Accounts"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li>
              <li>/</li>
              <li>{isArabic ? "الحسابات البنكية" : "Banks Accounts"}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={`bakacnt-wrap-1${isArabic ? " arabic-bank-accounts-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="bank-container">
            <div className="bank-intro">
              <div className="bank-intro-icon">
                <i className="fa-solid fa-hand-holding-heart"></i>
              </div>
              <div>
                <h2>{isArabic ? "اختر الحساب المناسب لتبرعك" : "Choose the right account for your donation"}</h2>
                <p>
                  {isArabic ? "يمكنك تحويل تبرعك مباشرةً عبر الحسابات البنكية الرسمية أدناه. يرجى استخدام نوع الحساب الصحيح لتبرعات الزكاة أو الصدقات." : "You can transfer your donation directly through the official bank accounts below. Please use the correct account type for Zakat or Sadakat donations."}
                </p>
              </div>
            </div>

            {loadError ? <div className="donation-error">{loadError}</div> : null}
            {isLoading ? <div className="donation-error">{isArabic ? "جارٍ تحميل الحسابات البنكية..." : "Loading bank accounts..."}</div> : null}

            <div className="bank-grid">
              {visibleAccounts.map((account) => (
                <div className="bank-card" key={account.id}>
                  <div className="bank-card-top">
                    <div className="bank-logo">
                      <i className="fa-solid fa-building-columns"></i>
                    </div>
                    <h3>{account.title}</h3>
                    <div className="bank-type">
                      <i className={`fa-solid ${getAccountIcon(account.categoryTitle)}`}></i>
                      {account.categoryTitle}
                    </div>
                  </div>

                  <div className="bank-card-body">
                    <div className="bank-detail">
                      <div className="bank-detail-top">
                        <label>{isArabic ? "رقم الحساب" : "Account Number"}</label>
                        <button className="copy-btn" type="button" title={isArabic ? "نسخ" : "Copy"} onClick={() => copyValue(account.accountNumber)}>
                          <i className={copiedValue === account.accountNumber ? "fa-solid fa-check" : "fa-regular fa-copy"}></i>
                        </button>
                      </div>
                      <strong>{account.accountNumber}</strong>
                    </div>

                    <div className="bank-detail">
                      <div className="bank-detail-top">
                        <label>{isArabic ? "رقم الآيبان" : "IBAN"}</label>
                        <button className="copy-btn" type="button" title={isArabic ? "نسخ" : "Copy"} onClick={() => copyValue(account.iban)}>
                          <i className={copiedValue === account.iban ? "fa-solid fa-check" : "fa-regular fa-copy"}></i>
                        </button>
                      </div>
                      <strong>{account.iban}</strong>
                    </div>

                    <Link to="/quick-donation" className="donate-action">
                      {isArabic ? "تبرع الآن" : "Donate Now"}
                      <i className={`fa-solid ${isArabic ? "fa-arrow-left" : "fa-arrow-right"}`}></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {!isLoading && !loadError && !visibleAccounts.length ? (
              <div className="donation-error">{isArabic ? "لا توجد حسابات بنكية متاحة حالياً." : "No bank accounts are available right now."}</div>
            ) : null}
          </div>
        </div>
      </section>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
